import React, { useState, useEffect, useMemo } from "react";
import { X } from "lucide-react";
import { ExtendedTemplate } from "../../../../views/marketing-pages/create-campaign/types";

interface TemplateVariableModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: ExtendedTemplate | null;
  // Called with the ordered array of variable values (for template_attributes.body)
  // and the fully rendered preview text (for templateDraft / display)
  onConfirm: (values: string[], renderedText: string) => void;
  // Optional initial values keyed by variable index (1-based)
  initialValues?: Record<number, string>;
}

export const TemplateVariableModal: React.FC<TemplateVariableModalProps> = ({
  isOpen,
  onClose,
  template,
  onConfirm,
  initialValues,
}) => {
  const sortedVars = useMemo(() => {
    if (!template?.variables) return [];
    return [...template.variables].sort((a, b) => a.index - b.index);
  }, [template]);

  const [values, setValues] = useState<Record<number, string>>({});

  useEffect(() => {
    if (!template) return;
    const initial: Record<number, string> = {};
    sortedVars.forEach((v) => {
      initial[v.index] =
        initialValues?.[v.index] ?? v.example ?? "";
    });
    setValues(initial);
  }, [template, isOpen]);

  if (!isOpen || !template) return null;

  const handleChange = (index: number, val: string) => {
    setValues((prev) => ({ ...prev, [index]: val }));
  };

  const allFilled = sortedVars.every((v) => (values[v.index] ?? "").trim() !== "");

  // Build live preview by replacing {{n}} placeholders with current values
  const previewText = (template.originalContent || template.preview || "").replace(
    /{{(\d+)}}/g,
    (match, idxStr) => {
      const idx = parseInt(idxStr, 10);
      const val = values[idx];
      return val && val.trim() !== "" ? val : match;
    },
  );

  const handleDone = () => {
    const orderedValues = sortedVars.map((v) => values[v.index] ?? "");
    onConfirm(orderedValues, previewText);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-[110] p-0 sm:p-4">
      <div className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[92vh] flex flex-col overflow-hidden shadow-2xl">
        {/* Drag handle (mobile) */}
        <div className="sm:hidden flex justify-center pt-2.5 pb-1">
          <div className="w-10 h-1 rounded-full bg-slate-200 dark:bg-gray-700" />
        </div>

        {/* Header */}
        <div className="px-5 sm:px-6 pt-2 sm:pt-5 pb-3 sm:pb-4 border-b dark:border-gray-800 flex items-start justify-between">
          <div>
            <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-gray-100">
              Configure Template
            </h2>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-gray-400 mt-0.5">
              Fill all required variables before sending.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300 transition-colors flex-shrink-0 -mt-1"
          >
            <X size={22} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4">
          {/* Preview */}
          <div className="bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl p-3.5 mb-5">
            <p className="text-sm text-slate-700 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
              {previewText}
            </p>
          </div>

          {/* Variable inputs */}
          <div className="flex flex-col gap-4">
            {sortedVars.map((v) => {
              const filled = (values[v.index] ?? "").trim() !== "";
              return (
                <div key={v.index}>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-sm font-semibold text-slate-700 dark:text-gray-200">
                      Variable {v.index} ({v.name || `var_${v.index}`})
                    </label>
                    {filled && (
                      <span className="text-xs font-semibold text-green-600">
                        Set
                      </span>
                    )}
                  </div>
                  <input
                    value={values[v.index] ?? ""}
                    onChange={(e) => handleChange(v.index, e.target.value)}
                    placeholder={v.example || ""}
                    className="w-full border border-slate-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 dark:focus:ring-indigo-500/30"
                  />
                  {v.example && (
                    <p className="text-[11px] text-slate-400 dark:text-gray-500 mt-1">
                      Example: {v.example}
                    </p>
                  )}
                </div>
              );
            })}

            {sortedVars.length === 0 && (
              <p className="text-sm text-slate-400 dark:text-gray-500 text-center py-4">
                This template has no variables to configure.
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 sm:px-6 py-4 border-t dark:border-gray-800 bg-white dark:bg-gray-900">
          <button
            onClick={handleDone}
            disabled={!allFilled}
            className="w-full bg-indigo-600 text-white rounded-xl py-3 font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};